import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Camera, Globe, Users, UserPlus, Trash2, MoreHorizontal, Shield, Loader2, Save, } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Button } from 'components/ui/button';
import Modal from 'components/ui/Modal';
import Avatar from 'components/ui/avatar';
import Badge from 'components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'components/ui';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubTrigger,
    DropdownMenuSubContent,
} from 'components/ui/dropdown-menu';
import { useAuthContext } from 'modules/auth';
import * as Organization from 'modules/organization';
import { toast } from 'components/ui/use-toast';
import { resolveOrgLogoSrc } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import type { Organization as OrganizationType, OrgRole } from '@examing/types';
import config from 'config';
import { WorkspaceGoalsCard } from 'components/organization/WorkspaceGoalsCard';
import { PERMISSIONS } from 'modules/auth/permissions';

const MAX_LOGO_SIZE = 5 * 1024 * 1024;
const ACCEPTED_LOGO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ROLE_OPTIONS: {
    value: string;
    labelKey: string;
}[] = [
    { value: 'ORG_ADMIN', labelKey: 'role_admin' },
    { value: 'AUTHOR', labelKey: 'role_author' },
    { value: 'PROCTOR', labelKey: 'role_proctor' },
];
const ROLE_BADGE_VARIANT: Record<string, 'primary' | 'success' | 'warning' | 'secondary'> = {
    SUPER_ADMIN: 'primary',
    ORG_ADMIN: 'primary',
    AUTHOR: 'success',
    PROCTOR: 'warning',
    CANDIDATE: 'secondary',
};
interface Member {
    id: string;
    role: OrgRole;
    joinedAt: string;
    user: {
        id: string;
        name: string;
        email: string;
        avatarUrl?: string;
        status?: string;
    } | null;
    // The org owner's role can't be changed and they can't be removed.
    isOwner?: boolean;
}
const OrganizationSettings: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { state: authState, methods: authMethods } = useAuthContext();
    // Redirect users without permission
    useEffect(() => {
        if (authState.isFetched && !authState.permissions.includes(PERMISSIONS.ROLES_EDIT)) {
            navigate('/app');
        }
    }, [authState.isFetched, authState.permissions, navigate]);
    // Full org fetched from API
    const [org, setOrg] = useState<OrganizationType | null>(null);
    const [isOrgLoading, setIsOrgLoading] = useState(true);
    // General settings form
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    // Members
    const [members, setMembers] = useState<Member[]>([]);
    const [isMembersLoading, setIsMembersLoading] = useState(false);
    // Add member modal
    const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
    const [newMemberEmail, setNewMemberEmail] = useState('');
    // Org member add is staff-only; candidates onboard via team join links.
    const [newMemberRole, setNewMemberRole] = useState('AUTHOR');
    const [isAddingMember, setIsAddingMember] = useState(false);
    // Logo upload
    const logoInputRef = useRef<HTMLInputElement>(null);
    const [logoUploading, setLogoUploading] = useState(false);
    // Fetch full org from API using the auth context's org id
    useEffect(() => {
        const orgId = authState.organization?.id;
        if (!orgId) {
            setIsOrgLoading(false);
            return;
        }
        setIsOrgLoading(true);
        Organization.Api.GetById(orgId)
            .then(({ data }: any) => {
            setOrg(data);
            setName(data.name || '');
            setSlug(data.slug || '');
        })
            .catch(() => {
            toast.error(t("failed_to_load_organization"));
        })
            .finally(() => setIsOrgLoading(false));
    }, [authState.organization?.id, t]);
    // Fetch members
    const fetchMembers = useCallback(async () => {
        if (!org)
            return;
        setIsMembersLoading(true);
        try {
            const result = await Organization.Api.GetMembers(org.id) as any;
            const data = Array.isArray(result.data) ? result.data : (result.data?.data || []);
            setMembers(data as Member[]);
        }
        catch {
            toast.error(t("failed_to_load_members"));
        }
        finally {
            setIsMembersLoading(false);
        }
    }, [org, t]);
    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);
    // Upload organization logo
    const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !org) return;
        if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
            toast.error(t('invalid_file_type'), { description: t('please_upload_a_jpeg_png_webp_or_gif_image') });
            return;
        }
        if (file.size > MAX_LOGO_SIZE) {
            toast.error(t('file_too_large'), { description: t('avatar_must_be_5_mb_or_smaller') });
            return;
        }
        setLogoUploading(true);
        try {
            const { data } = await Organization.Api.UploadLogo(org.id, file) as any;
            setOrg({ ...org, logoUrl: data.logoUrl });
            // Propagate to the auth context so the sidebar OrgSwitcher (active org
            // button + dropdown row) refreshes without a full reload.
            if (authState.organization?.id === org.id) {
                authMethods.setOrganization({ ...authState.organization, logoUrl: data.logoUrl });
            }
            authMethods.setOrganizations(
                authState.organizations.map((o) => (o.id === org.id ? { ...o, logoUrl: data.logoUrl } : o))
            );
            toast.success(t('organization_updated_successfully'));
        }
        catch (err: any) {
            const message = err?.response?.data?.message || 'Upload failed';
            toast.error(message);
        }
        finally {
            setLogoUploading(false);
        }
    };
    // Save general settings
    const handleSave = async () => {
        if (!org)
            return;
        if (!name.trim()) {
            toast.error(t("organization_name_is_required"));
            return;
        }
        if (!slug.trim()) {
            toast.error(t("workspace_slug_is_required"));
            return;
        }
        setIsSaving(true);
        try {
            const { data: updated } = await Organization.Api.Update(org.id, {
                name: name.trim(),
                slug: slug.trim().toLowerCase().replace(/\s+/g, '-'),
            }) as any;
            setOrg({ ...org, ...updated });
            // Keep auth context in sync
            authMethods.setOrganization({ id: updated.id, name: updated.name, slug: updated.slug });
            toast.success(t("organization_updated_successfully"));
        }
        catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Update failed';
            toast.error(message);
        }
        finally {
            setIsSaving(false);
        }
    };
    // Add member
    const handleAddMember = async () => {
        if (!org || !newMemberEmail.trim())
            return;
        if (!EMAIL_PATTERN.test(newMemberEmail.trim())) {
            toast.error(t('invalid_email_address', 'Enter a valid email address.'));
            return;
        }
        setIsAddingMember(true);
        try {
            await Organization.Api.AddMember(org.id, {
                email: newMemberEmail.trim(),
                role: newMemberRole,
            });
            toast.success(t("member_added_successfully"));
            setIsAddMemberOpen(false);
            setNewMemberEmail('');
            setNewMemberRole('AUTHOR');
            fetchMembers();
        }
        catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Failed to add member';
            toast.error(message);
        }
        finally {
            setIsAddingMember(false);
        }
    };
    // Update member role
    const handleUpdateRole = async (memberId: string, role: string) => {
        if (!org)
            return;
        try {
            await Organization.Api.UpdateMemberRole(org.id, memberId, { role });
            setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, role: role as OrgRole } : m)));
            toast.success(t("role_updated"));
        }
        catch {
            toast.error(t("failed_to_update_role"));
        }
    };
    // Remove member
    const handleRemoveMember = async (memberId: string) => {
        if (!org)
            return;
        try {
            await Organization.Api.RemoveMember(org.id, memberId);
            setMembers((prev) => prev.filter((m) => m.id !== memberId));
            toast.success(t("member_removed"));
        }
        catch {
            toast.error(t("failed_to_remove_member"));
        }
    };
    if (isOrgLoading) {
        return (<div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-muted-foreground"/>
      </div>);
    }
    if (!org) {
        return (<div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">{t("no_organization_found")}</p>
      </div>);
    }
    return (<div className="w-full max-w-3xl space-y-8">
      {/* No page-level heading here — this page is only hosted inside the
          settings dialog, whose section title (SettingsDialog.tsx) is the
          single source of truth for the "Organization" heading. */}
      {/* General */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("general")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
              <div className="relative flex-shrink-0">
                <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                  {org.logoUrl ? (<img src={resolveOrgLogoSrc(config.api.base_url, org.id, org.logoUrl)} alt={org.name} className="w-full h-full object-cover rounded-lg"/>) : (<Building2 size={28} className="text-muted-foreground" aria-hidden/>)}
                </div>
                {authState.permissions.includes(PERMISSIONS.ROLES_EDIT) && (
                  <>
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={logoUploading}
                      aria-label={t('change_photo')}
                      className="absolute -bottom-1 -right-1 w-7 h-7 bg-primary text-primary-foreground rounded-lg flex items-center justify-center shadow-md hover:bg-primary/90 transition-colors disabled:opacity-60"
                    >
                      {logoUploading ? <Loader2 size={13} className="animate-spin"/> : <Camera size={13}/>}
                    </button>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                      className="hidden"
                      onChange={handleLogoSelect}
                    />
                  </>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{org.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{org.tier} {t("plan")}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("organization_name")}</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground"/>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("organization_name_2")} className="pl-9"/>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("workspace_slug")}</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-3 h-4 w-4 text-muted-foreground"/>
                <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder={t("your-workspace")} className="pl-9"/>
              </div>
              <p className="text-xs text-muted-foreground">{t("examing_app_org")}{slug || t("your-workspace")}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Onboarding answers that steer template suggestions. The API gates org
          updates on MEMBERS_EDIT, so the card follows that key. */}
      {authState.permissions.includes(PERMISSIONS.MEMBERS_EDIT) && (
        <WorkspaceGoalsCard org={org} onSaved={(updated) => setOrg({ ...org, ...updated })} />
      )}

      {/* Members */}
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users size={18}/>{t("members")}<span className="text-sm font-normal text-muted-foreground">({members.length})</span>
          </CardTitle>
          {authState.permissions.includes(PERMISSIONS.MEMBERS_EDIT) && (<Button size="sm" className="w-full sm:w-auto" onClick={() => setIsAddMemberOpen(true)}>
              <UserPlus size={14} className="mr-2"/>{t("add_member")}</Button>)}
        </CardHeader>
        <CardContent>
          {isMembersLoading ? (<div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-muted-foreground"/>
            </div>) : members.length === 0 ? (<p className="text-center py-8 text-muted-foreground text-sm">{t("no_members_yet")}</p>) : (<div className="space-y-2">
              {members.map((member) => (<div key={member.id} className="flex flex-col gap-3 rounded-lg p-3 transition-colors hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar name={member.user?.name || 'Unknown'} src={member.user?.avatarUrl} size="sm"/>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {member.user?.name || 'Unknown'}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{member.user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Badge variant={ROLE_BADGE_VARIANT[member.role] || 'secondary'} size="sm">
                      {member.role.replace('_', ' ')}
                    </Badge>
                    {authState.permissions.includes(PERMISSIONS.MEMBERS_EDIT) && !member.isOwner && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button type="button" aria-label={t('member_actions', 'Member actions')} className="p-1 rounded-md hover:bg-muted transition-colors">
                            <MoreHorizontal size={16} className="text-muted-foreground"/>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger className="flex items-center gap-2">
                              <Shield size={14}/>
                              {t("change_role")}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              {ROLE_OPTIONS.map((opt) => (
                                <DropdownMenuItem key={opt.value} disabled={member.role === opt.value} onClick={() => handleUpdateRole(member.id, opt.value)}>
                                  {t(opt.labelKey)}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleRemoveMember(member.id)} className="flex items-center gap-2 text-destructive focus:text-destructive">
                            <Trash2 size={14}/>
                            {t("remove")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>))}
            </div>)}
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button variant="outline" className="w-full sm:w-auto" onClick={() => {
            setName(org.name);
            setSlug(org.slug);
        }}>{t("cancel")}</Button>
        <Button className="w-full sm:w-auto" onClick={handleSave} disabled={isSaving}>
          {isSaving ? (<Loader2 size={14} className="mr-2 animate-spin"/>) : (<Save size={14} className="mr-2"/>)}{t("save_changes_2")}</Button>
      </div>

      {/* Add Member Modal */}
      <Modal isOpen={isAddMemberOpen} onClose={() => {
            setIsAddMemberOpen(false);
            setNewMemberEmail('');
            setNewMemberRole('AUTHOR');
        }} title={t("add_member")} description={t("invite_user_to_organization_by_email")} size="sm">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("email_address")}</Label>
            <Input type="email" placeholder={t("member_example_com")} value={newMemberEmail} onChange={(e) => setNewMemberEmail(e.target.value)}/>
          </div>
          <div className="space-y-2">
            <Label>{t("role")}</Label>
            <Select value={newMemberRole} onValueChange={setNewMemberRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((opt) => (<SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => {
            setIsAddMemberOpen(false);
            setNewMemberEmail('');
            setNewMemberRole('AUTHOR');
        }}>{t("cancel")}</Button>
            <Button className="w-full sm:w-auto" onClick={handleAddMember} disabled={isAddingMember || !newMemberEmail.trim()}>
              {isAddingMember && <Loader2 size={14} className="mr-2 animate-spin"/>}{t("add_member")}</Button>
          </div>
        </div>
      </Modal>

    </div>);
};
export default OrganizationSettings;
